import { z } from 'zod'

/**
 * New-account outbound send limit for the signed-in account.
 *
 * Note what is deliberately absent: there is no operator throttle reason here,
 * and `decision_note` is only populated for a denial. A throttled account is
 * still a signed-in account, so serving the operator's abuse notes back to it
 * would disclose what we detect and at what threshold.
 *
 * `window_active` is separate from `restricted` on purpose. An approved
 * in-window account has `restricted: false` but still needs the card to say the
 * lift was granted, and after day 30 the same account should fall through to
 * showing nothing.
 */
export const MailSendLimitStatus = z.object({
  restricted: z.boolean(),
  forced_restricted: z.boolean(),
  window_active: z.boolean(),
  hourly_cap: z.number(),
  daily_cap: z.number(),
  window_days: z.number(),
  restricted_until: z.string().nullish(),
  request_status: z.enum(['none', 'pending', 'approved', 'denied']),
  requested_at: z.string().nullish(),
  decided_at: z.string().nullish(),
  decision_note: z.string().nullish(),
  can_request: z.boolean(),
  request_cooldown_until: z.string().nullish()
})
export type MailSendLimitStatus = z.infer<typeof MailSendLimitStatus>

/** Admin queue row. Unlike the customer view this does carry forced_reason. */
export const MailSendLimitRequestRow = z.object({
  account_id: z.number(),
  email: z.string(),
  name: z.string(),
  created: z.string().nullish(),
  age_days: z.number(),
  request_status: z.enum(['none', 'pending', 'approved', 'denied']),
  requested_at: z.string().nullish(),
  reason: z.string().nullish(),
  email_verified: z.boolean(),
  has_payment_method: z.boolean(),
  mailbox_count: z.number(),
  outbound_7d: z.number(),
  forced_restricted: z.boolean(),
  forced_reason: z.string().nullish()
})
export type MailSendLimitRequestRow = z.infer<typeof MailSendLimitRequestRow>

export const MailSendLimitDecisionResult = z.object({
  status: MailSendLimitStatus,
  /**
   * This account's mailboxes in the restricted set. A zero on a throttle means
   * it reached nothing, which is worth surfacing to the operator at the moment
   * of use rather than during a post-mortem.
   */
  affected_senders: z.number(),
  guard_sync: z.enum(['ok', 'deferred', 'failed'])
})
export type MailSendLimitDecisionResult = z.infer<typeof MailSendLimitDecisionResult>
